import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, List, Collapse, ListItemText, ListItemIcon, ListItemButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
//
import Iconify from './Iconify';
import { chatUrl } from 'src/Api';
import palette from 'src/theme/palette';
// ----------------------------------------------------------------------

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.body2,
    position: 'relative',
    textTransform: 'capitalize',
    color: '#FFFFFF',
    paddingLeft: 0,
    paddingRight: 0,
    height: 30,
    marginTop: 5,
    '&:before': {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: "''",
      display: 'none',
      position: 'absolute',
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4
    }
  })
);

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func
};

function NavItem({ item, active }) {
  const theme = useTheme();
  const { title, path, icon, subRoutes = [] } = item;

  const hasSubRoutes = subRoutes.length > 0;

  // Parent active if itself or any child active
  const isActiveMain = active(path) || subRoutes.some((child) => active(child.path));

  // Auto-open if any child is active
  const [open, setOpen] = useState(isActiveMain);

  const listItemBaseStyle = {
    color: '#FFFFFF', // White color
    '&:hover': { bgcolor: palette.secondary.dashboardChildBackHover, borderRadius: 0.5 }
  };

  const activeMainStyle = {
    bgcolor: palette.primary.dashboardChildBack,
    color: palette.primary.titledark,
    borderRadius: 0.5,
    '&:hover': { bgcolor: palette.primary.dashboardChildBack }
  };

  const parentActiveMainStyle = {
    bgcolor: palette.primary.secondaryMain,
    color: palette.primary.main,
    borderRadius: 0.5,
    '&:hover': { bgcolor: palette.primary.secondaryMain }
  };

  const signleActiveMainStyle = {
    bgcolor: palette.primary.secondaryMainLighter,
    color: palette.primary.titledark,
    borderRadius: 0.5,
    '&:hover': { bgcolor: palette.primary.secondaryMainLight }
  };

  const activeChildStyle = {
    bgcolor: palette.primary.dashboardChildBack,
    color: palette.primary.titledark,
    borderRadius: 0.5,
    '&:hover': { bgcolor: palette.primary.dashboardChildBack }
  };

  return (
    <>
      {/* Main Route */}
      <ListItemStyle
        onClick={hasSubRoutes ? () => setOpen(!open) : undefined}
        component={!hasSubRoutes ? RouterLink : 'div'}
        to={!hasSubRoutes ? path : undefined}
        sx={{
          ...listItemBaseStyle,
          ...(open && activeMainStyle),
          ...(open && hasSubRoutes && parentActiveMainStyle),
          ...(isActiveMain && !hasSubRoutes && signleActiveMainStyle),
          px: 1,
          position: 'relative'
        }}
      >
        <ListItemIconStyle>
          <Iconify
            icon={icon}
            sx={{
              color: '#FFFFFF', // White color for icon
              fontSize: 18
            }}
          />
        </ListItemIconStyle>

        <ListItemText
          disableTypography
          primary={title}
          sx={{
            textAlign: 'left',
            ml: -1,
            fontSize: 11,
            fontFamily: isActiveMain ? 'poppinsMedium' : 'poppinsRegular',
            color: '#FFFFFF', // White color for text
            mt: 0.4
          }}
        />

        {hasSubRoutes && (
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{
              width: 13,
              height: 13,
              ml: 1,
              color: '#FFFFFF' // White color for arrow icon
            }}
          />
        )}
      </ListItemStyle>

      {/* Sub Routes */}
      {hasSubRoutes && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subRoutes.map((child, index) => {
              const isActiveSub = active(child.path);
              return (
                <ListItemStyle
                  key={child.title}
                  component={RouterLink}
                  to={child.path}
                  sx={{
                    pl: 1.5,
                    ml: 3.2,
                    borderRadius: 0.5,
                    ...(isActiveSub && activeChildStyle)
                  }}
                >
                  <ListItemText
                    disableTypography
                    primary={child.title}
                    sx={{
                      fontSize: 11,
                      fontFamily: isActiveSub ? 'poppinsMedium' : 'poppinsRegular',
                      color: isActiveSub
                        ? palette.primary.titledark
                        : '#FFFFFF' // White color for child text
                    }}
                  />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array
};

export default function NavSection({ navConfig, ...other }) {
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  return (
    <Box key={other} {...other}>
      <List disablePadding>
        {navConfig.map((item) => (
          <>
            <NavItem key={item.title} item={item} active={match} />
          </>
        ))}
      </List>
    </Box>
  );
}